import './AdView.sass';

import { FC, useMemo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Header,
  InfoBlock,
  InlineHeader,
  List,
  ListItem,
  LottieIcon,
  Placeholder,
  ScrollView,
} from '$uikit';
import { Loader, MetaTags, ModalLink } from '$shared/components';
import { useTranslator } from '$hooks';
import { Api } from '$api';
import { toastActions } from '$store/toast';
import { postsActions } from '$store/posts';
import { adsActions, adsSelector } from '$store/ads';
import { batchActions } from '$store';
import { AdModel, AdStatsModel, AdStatus, CurrencyEnum } from '$store/models';
import { adsStatus } from '$utils/ads';
import { formatAmount } from '$utils/number';
import { showModal } from '$navigation/helper';
import { ADS_BALANCE } from '$shared/constants/modals';
import { format, _confirm, formatCurrency } from '$utils';
import { IcEdit28 } from '$assets';

export interface AdViewProps {
  adId: number;
}

export const AdView: FC<AdViewProps> = ({ adId }) => {
  const t = useTranslator();
  const dispatch = useDispatch();

  const [loadingStatus, setLoadingStatus] = useState('');
  const { ads, balance, placeBalance } = useSelector(adsSelector);
  const [avgPrice, setAvgPrice] = useState(0.5);
  const [adStats, setAdStats] = useState<AdStatsModel[]>([]);
  const [revenue, setRevenue] = useState<number>(0);

  const ad = useMemo(() => {
    return ads.find((ad) => ad.id === adId);
  }, [adId, ads]);

  const loadData = useCallback(() => {
    setLoadingStatus('loading');
    Api.get(`/ads/${adId}/info`)
      .then((data: any) => {
        const isPlace = data.ads?.currency === CurrencyEnum.PLACE;
        setAdStats(data.stats || []);
        setAvgPrice(isPlace ? data.avgPricePlace ?? data.avgPrice : data.avgPrice);
        setRevenue(data.revenue);
        dispatch(
          batchActions(
            postsActions.setPosts([data.post]),
            adsActions.setAd(data.ads),
            adsActions.setAdsBalance(data.balance),
            adsActions.setPlaceBalance(data.placeBalance ?? 0),
          ),
        );
        setLoadingStatus('');
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message));
        setLoadingStatus('failed');
      });
  }, [adId, dispatch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loadingStatus === 'loading') {
    return <Loader isWrapped />;
  }
  if (loadingStatus === 'failed') {
    return (
      <Placeholder
        title={t('failed_title')}
        actions={
          <Button size="small" onClick={loadData}>
            {t('failed_button')}
          </Button>
        }
      >
        {t('failed_caption')}
      </Placeholder>
    );
  }
  if (!ad) {
    return null;
  }

  const currency = ad.currency || CurrencyEnum.TON;
  const lowFunds = ad.price > (currency === CurrencyEnum.PLACE ? placeBalance : balance);

  const handleStatusClick = () => {
    let newStatus = 0;
    let text: string | undefined;
    if (ad.status === AdStatus.none || ad.status === AdStatus.declined) {
      newStatus = AdStatus.inReview;
      text = t('ads_view_confirm_send_to_review');
    } else if (ad.status === AdStatus.paused || ad.status === AdStatus.approved) {
      newStatus = AdStatus.active;
      text = t('ads_view_confirm_start');
    } else if (ad.status === AdStatus.inReview) {
      newStatus = AdStatus.none;
      text = t('ads_view_confirm_cancel_review');
    } else if (ad.status === AdStatus.active) {
      newStatus = AdStatus.paused;
      text = t('ads_view_confirm_stop');
    }

    if (!text) {
      return;
    }

    if (newStatus === AdStatus.active && lowFunds) {
      if (currency !== CurrencyEnum.PLACE) showModal(ADS_BALANCE);
      return;
    }

    _confirm(text, () => {
      dispatch(toastActions.setToastLoading());
      Api.post(`/ads/${adId}/status`, { status: newStatus })
        .then(({ ad: updated }: any) => {
          dispatch(
            batchActions(toastActions.setToastSuccess(), adsActions.setAd(updated)),
          );
        })
        .catch((e) => dispatch(toastActions.setToastFail(e.message)));
    });
  };

  return (
    <>
      <MetaTags title={getAdTitle(ad, true)} />
      <Header showBackButton>{getAdTitle(ad)}</Header>

      <ScrollView>
        <div className="AdView__content">
          {lowFunds &&
            (currency === CurrencyEnum.PLACE ? (
              <InfoBlock type="warning">{t('ads_view_balance_error')}</InfoBlock>
            ) : (
              <ModalLink name={ADS_BALANCE}>
                <InfoBlock type="warning" arrow>
                  {t('ads_view_balance_error')}
                </InfoBlock>
              </ModalLink>
            ))}

          <List>
            <ListItem
              onClick={handleStatusClick}
              chevron
              indicator={
                <span className={`AdView__status_${ad.status}`}>
                  {adsStatus(ad.status)}
                </span>
              }
            >
              {t('ads_view_status')}
            </ListItem>
            <ListItem indicator={formatAmount(ad.totalSpend, currency)}>
              {t('ads_view_total_spend')}
            </ListItem>
            <ListItem indicator={`${ad.conversions}`}>
              {t('ads_view_conversions')}
            </ListItem>
            <ListItem indicator={formatCurrency(revenue, CurrencyEnum.EUR)}>
              {t('ads_view_revenue')}
            </ListItem>
            <ListItem chevron onClick={() => showModal(`ad_users${adId}`)}>
              {t('ads_view_show_users')}
            </ListItem>
          </List>

          <Button
            mode="secondary"
            onClick={() =>
              ad.objectType === 'post'
                ? showModal(`post${ad.objectId}`)
                : showModal(`ad_preview${ad.id}`)
            }
          >
            {ad.objectType === 'post'
              ? t('ads_view_open_post')
              : t('ads_view_open_preview')}
          </Button>
        </div>

        <InlineHeader type="sub">{t('ads_view_targeting')}</InlineHeader>
        <div className="AdView__content">
          <List>
            <ListItem
              indicator={
                ad.targetSubscribed ? t('ads_view_paying_users') : t('ads_view_all_users')
              }
            >
              {t('ad_targeting_show_to_label')}
            </ListItem>
            <ListItem
              indicator={formatAmount(ad.price, currency)}
              description={
                ad.price < avgPrice ? (
                  <span className="AdView__status orange">
                    {t('ads_view_low_price_warning', {
                      avgPrice: formatAmount(avgPrice, currency, { symbol: false }),
                      currency: currency.toUpperCase(),
                    })}
                  </span>
                ) : undefined
              }
            >
              {t('ads_view_price')}
            </ListItem>
            <ListItem indicator={formatAmount(ad.spendLimit, currency)}>
              {t('ads_view_spend_limit')}
            </ListItem>
            <ListItem
              indicator={ad.targetLang.toUpperCase() || t('ad_targeting_lang_all')}
            >
              {t('ad_targeting_lang_label')}
            </ListItem>
          </List>

          <Button
            mode="secondary"
            iconRight={<IcEdit28 />}
            onClick={() =>
              ad.objectType === 'post'
                ? showModal(`promote${ad.objectId}`)
                : showModal(`ad_targeting${ad.id}`)
            }
          >
            {ad.objectType === 'post' ? t('ads_view_edit') : t('ads_view_edit_targeting')}
          </Button>
        </div>

        <InlineHeader type="sub">{t('ads_view_stats')}</InlineHeader>
        <div className="AdView__content">
          {adStats.length > 0 ? (
            <List>
              {adStats.map((item, i) => {
                const [year, month, day] = item.date.split('-').map(Number);
                const date = [
                  year,
                  String(month).padStart(2, '0'),
                  String(day).padStart(2, '0'),
                ].join('/');

                return (
                  <ListItem
                    key={i}
                    indicator={date}
                    description={t('ads_view_stats_spend', {
                      amount: formatAmount(item.spend, currency, { symbol: false }),
                      currency: currency.toUpperCase(),
                    })}
                  >
                    {t('ads_view_stats_conversions', { conversions: item.total })}
                  </ListItem>
                );
              })}
            </List>
          ) : (
            <Placeholder
              className="AdView__stats_placeholder"
              title={t('ads_view_stats_placeholder_title')}
              icon={<LottieIcon name="graph" />}
              iconSize={80}
            >
              {t('ads_view_stats_placeholder_text')}
            </Placeholder>
          )}
        </div>
      </ScrollView>
    </>
  );
};

export function getAdTitle({ objectType, createdAt }: AdModel, full?: boolean) {
  let title = '';
  if (objectType === 'profile') {
    title = 'Profile';
  } else if (objectType === 'promo_post' || objectType === 'post') {
    title = 'Post';
  } else if (objectType === 'reels') {
    title = 'Reels';
  }
  title += full ? ' ad' : '';
  title += ': ' + format(createdAt * 1000, 'dd MMM, HH');
  return title;
}
